import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Calendar, FileText, MapPin } from "lucide-react";
import { DecorativeLines } from "@/components/DecorativeLines";

const stats = [
  {
    key: "years",
    icon: Calendar,
    value: 20,
    suffix: "+",
    label: "Lat doświadczenia",
    description: "Na rynku serwisu medycznego",
  },
  {
    key: "reports",
    icon: FileText,
    value: 6000,
    suffix: "+",
    label: "Raportów serwisowych rocznie",
    description: "",
  },
  {
    key: "poland",
    icon: MapPin,
    value: null,
    suffix: "",
    label: "Działamy na terenie całej Polski",
    description: "",
  },
];

const CountUp = ({ end, suffix, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return (
    <span ref={ref} className="stat-number">
      {count}{suffix}
    </span>
  );
};

export const WhyUs = () => {
  return (
    <section
      id="dlaczego-my"
      className="section-spacing bg-white dark:bg-[#121418] relative"
      aria-label="Dlaczego my"
      data-testid="whyus-section"
    >
      <DecorativeLines variant="curves-right" />
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="text-[#EA8628] font-semibold text-sm uppercase tracking-wider">
            Dlaczego my
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#212738] dark:text-white">
            Zaufaj profesjonalistom
          </h2>
          <p className="mt-4 text-[#504D4D] dark:text-white/70">
            Nasze liczby mówią same za siebie. Jesteśmy sprawdzonym partnerem.
          </p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="relative p-6 rounded-xl bg-[#F7F9F9] dark:bg-[#171A1F] border border-gray-200 dark:border-white/5 text-center group hover:border-[#EA8628]/30 transition-all duration-200 shadow-sm dark:shadow-none"
              data-testid={`whyus-stat-${stat.key}`}
            >
              <div className="w-14 h-14 mx-auto rounded-xl bg-[#EA8628]/10 flex items-center justify-center group-hover:bg-[#EA8628]/20 transition-colors duration-200">
                <stat.icon className="w-7 h-7 text-[#EA8628]" />
              </div>
              {stat.value !== null && (
                <div className="mt-5 text-4xl lg:text-5xl font-bold text-[#212738] dark:text-white">
                  <CountUp end={stat.value} suffix={stat.suffix} />
                </div>
              )}
              <h3 className={`${stat.value !== null ? 'mt-2' : 'mt-5'} text-lg font-semibold text-[#212738] dark:text-white`}>
                {stat.label}
              </h3>
              {stat.description && (
                <p className="mt-1 text-sm text-[#504D4D] dark:text-white/50">
                  {stat.description}
                </p>
              )}
            </motion.div>
          ))}
        </div>

        {/* Additional trust section */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-[#EA8628]/10 to-transparent border border-[#EA8628]/20"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-[#212738] dark:text-white">
                Część GrupaQ
              </h3>
              <p className="mt-3 text-[#504D4D] dark:text-white/70 leading-relaxed">
                CKK ResQ jest częścią GrupaQ — dynamicznie rozwijającej się grupy firm medycznych 
                oferującej kompleksowe rozwiązania dla sektora ochrony zdrowia w Polsce.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 justify-start md:justify-end">
              <span className="px-4 py-2 rounded-full bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-[#504D4D] dark:text-white/80 text-sm">
                ResQmed
              </span>
              <span className="px-4 py-2 rounded-full bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-[#504D4D] dark:text-white/80 text-sm">
                IQmed
              </span>
              <span className="px-4 py-2 rounded-full bg-[#EA8628]/20 border border-[#EA8628]/30 text-[#EA8628] text-sm font-medium">
                CKK ResQ
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
